import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {captureException} from '@sentry/browser';
import {isFeatureEnabled} from 'feature-flags';
import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isEmpty} from 'ramda';

import {
  sourcingApi,
  sourcingAuctionApi,
  useAuctionPushVehiclesToSaleMutation,
  useLazySourcingAvailabilityCheckQuery,
  useSourcingPushVehiclesToSaleMutation,
} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri} from '@dms/routes';
import {COUNTRY_CZE, handleApiError} from '@dms/shared';

import {ApiError, pushGtmEvent, useOnMount} from 'shared/web';

import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {UnconfirmedAvailabilityError} from '../constants/UnconfirmedAvailabilityError';
import {collectUserListVehicleIds} from '../utils/collectUserListVehicleIds';
import {filterOutSoldAdIds} from '../utils/filterOutSoldAdIds';
import {isVehicleEligibleToPushToSale} from '../utils/isVehicleEligibleToPushToSale';

class BuyAllCancelled extends Error {}

const isApiError = (error: unknown): error is ApiError => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  if ('status' in error) {
    return (
      typeof error.status === 'number' ||
      error.status === 'FETCH_ERROR' ||
      error.status === 'PARSING_ERROR' ||
      error.status === 'TIMEOUT_ERROR' ||
      error.status === 'CUSTOM_ERROR'
    );
  }
  return 'name' in error && typeof error.name === 'string';
};

export function useBuyAllToBuyVehicles() {
  const store = useStore<Parameters<typeof selectActiveBranchId>[0]>();
  const apiDispatch =
    useDispatch<
      ThunkDispatch<Parameters<typeof selectActiveBranchId>[0], unknown, UnknownAction>
    >();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();
  const sourcingType = useSourcingType();
  const [checkAvailability] = useLazySourcingAvailabilityCheckQuery();
  const [pushClassifiedsToSale, {isLoading: isBuyingClassifieds}] =
    useSourcingPushVehiclesToSaleMutation();
  const [pushAuctionsToSale, {isLoading: isBuyingAuctions}] =
    useAuctionPushVehiclesToSaleMutation();
  const isBuying = isBuyingClassifieds || isBuyingAuctions;
  const [isCollectingVehicles, setIsCollectingVehicles] = useState(false);
  const isMountedRef = useRef(true);

  useOnMount(
    () => {
      isMountedRef.current = true;
    },
    () => {
      isMountedRef.current = false;
    }
  );

  const buyAll = () => {
    if (isCollectingVehicles || isBuying) {
      return;
    }

    // Branch/workspace can change while pages and availability are awaited. Read the live store
    // here rather than closing over a render-time selector value.
    const getContext = () =>
      JSON.stringify([selectActiveBranchId(store.getState()), getWorkspaceFromUri().workspace]);
    const initialContext = getContext();
    const assertContext = () => {
      if (!isMountedRef.current || getContext() !== initialContext) {
        throw new BuyAllCancelled();
      }
    };
    const params = {
      currency: currency?.code ?? DEFAULT_CURRENCY_CODE,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      pricingStrategy,
    };

    // Dedicated one-page queries keep Buy all from appending to the visible infinite-list cache.
    const fetchClassifiedsPage = (offset: number) =>
      apiDispatch(
        sourcingApi.endpoints.getSourcingUserToBuyPage.initiate(
          {...params, offset},
          {subscribe: false, forceRefetch: true}
        )
      ).unwrap();

    const fetchAuctionsPage = (offset: number) =>
      apiDispatch(
        sourcingAuctionApi.endpoints.getAuctionUserToBuyPage.initiate(
          {...params, offset},
          {subscribe: false, forceRefetch: true}
        )
      ).unwrap();

    setIsCollectingVehicles(true);
    const ids =
      sourcingType === SourcingType.CLASSIFIEDS
        ? collectUserListVehicleIds(
            fetchClassifiedsPage,
            (row) =>
              isVehicleEligibleToPushToSale(sourcingType, row.sourcingVehicle)
                ? row.sourcingVehicle.adId
                : undefined,
            assertContext
          )
        : collectUserListVehicleIds(
            fetchAuctionsPage,
            (row) => row.auctionVehicle.adId ?? undefined,
            assertContext
          );

    ids
      .then(async (externalIds) => {
        assertContext();
        const collectedCount = externalIds.length;
        if (
          sourcingType === SourcingType.CLASSIFIEDS &&
          isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK)
        ) {
          externalIds = await filterOutSoldAdIds(
            externalIds,
            (adIds) => checkAvailability({adIds}).unwrap(),
            assertContext
          );
        }

        assertContext();
        const soldCount = collectedCount - externalIds.length;
        const soldNotice =
          soldCount > 0
            ? i18n.t('entity.vehicle.notifications.soldVehiclesSkippedInBuyAll', {
                count: soldCount,
              })
            : undefined;
        if (isEmpty(externalIds)) {
          showNotification.info(soldNotice ?? i18n.t('entity.vehicle.notifications.noVehicles'));
          return;
        }
        const pushVehiclesToSale =
          sourcingType === SourcingType.AUCTIONS ? pushAuctionsToSale : pushClassifiedsToSale;
        return pushVehiclesToSale({externalIds})
          .unwrap()
          .then(() => {
            pushGtmEvent({
              event: `sourcing_${sourcingType}_buy_pushed_to_sale`,
              event_id: `sourcing_${sourcingType}_buy_pushed_to_sale`,
            });
            showNotification.success(i18n.t('entity.vehicle.notifications.vehiclesPushedToSales'));
            if (soldNotice !== undefined) {
              showNotification.info(soldNotice);
            }
          });
      })
      .catch((error) => {
        const reportUnexpectedError = (unexpectedError: unknown) => {
          captureException(unexpectedError);
          showNotification.error(i18n.t('general.labels.somethingWentWrong'));
        };
        match<unknown>(error)
          .when(
            (value): value is BuyAllCancelled => value instanceof BuyAllCancelled,
            () => undefined
          )
          .when(
            (value): value is UnconfirmedAvailabilityError =>
              value instanceof UnconfirmedAvailabilityError,
            (availabilityError) =>
              showNotification.error(
                i18n.t('entity.vehicle.notifications.buyAllAvailabilityUnconfirmed', {
                  count: availabilityError.adIds.length,
                })
              )
          )
          .when((value): value is Error => value instanceof Error, reportUnexpectedError)
          .when(isApiError, handleApiError)
          .otherwise(reportUnexpectedError);
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsCollectingVehicles(false);
        }
      });
  };

  return {buyAll, isCollectingVehicles, isBuying};
}
